import React, { useState, useRef, useEffect } from "react";
import { DotsVerticalIcon } from "@heroicons/react/solid";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { Button } from "../../components/ui";

const ChatHeader = ({
  selectedChat,
  closeChat,
  handleNeedsAttention,
  isSocketConnected = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // Get first and last name from other_member
  const firstName = selectedChat.other_member?.first_name || "";
  const lastName = selectedChat.other_member?.last_name || "";
  const displayName = `${firstName} ${lastName}`.trim();

  // Create initials for avatar if no image
  const initials =
    firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase();

  // Check if there's an active needs attention request
  const hasActiveAttentionRequest = selectedChat.chat?.is_needs_attention === 1;

  // Create appropriate tooltip message based on state
  const attentionButtonTooltip = !isSocketConnected
    ? "Connect to chat first"
    : hasActiveAttentionRequest
    ? "Attention request already active"
    : "Request urgent attention";

  return (
    <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-100">
      <div className="flex items-center space-x-3">
        {selectedChat.other_member?.image_path ? (
          <img
            src={selectedChat.other_member.image_path}
            alt={displayName}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gray-700 text-white flex text-lg items-center justify-center font-medium">
            {initials}
          </div>
        )}
        <div>
          <h3 className="font-medium">{displayName}</h3>
          {/* <div className="flex items-center">
            <span 
              className={`w-2 h-2 rounded-full mr-1 ${isSocketConnected ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            <span className="text-xs text-gray-500">
              {isSocketConnected ? 'Connected' : 'Connecting...'}
            </span>
          </div> */}
        </div>
      </div>
      <div className="flex items-center">
        {/* Enhanced Request Attention Button */}
        {hasActiveAttentionRequest ? (
          <Button
            disabled
            className="gap-2 rounded-full text-sm
              bg-amber-500 text-white cursor-not-allowed mr-2 disabled:opacity-100"
            title="Attention request active"
          >
            <div className="relative">
              <ExclamationCircleIcon className="w-4 h-4" />
              <span className="absolute top-0 right-0 w-4 h-4 bg-white rounded-full animate-ping"></span>
            </div>
            <span>Attention Requested</span>
          </Button>
        ) : (
          <Button
            onClick={handleNeedsAttention}
            className={`gap-2 rounded-full text-sm mr-2
              ${
                !isSocketConnected
                  ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                  : ""
              }`}
            title={attentionButtonTooltip}
            disabled={!isSocketConnected}
          >
            <img
              src="/assets/icons/needs-attention.png"
              alt=""
              className={`w-4 h-4 ${!isSocketConnected ? "opacity-50" : ""}`}
            />
            Request Attention
          </Button>
        )}

        <div className="relative" ref={menuRef}>
          <Button
            className="hover:bg-gray-200 rounded-full"
            onClick={toggleMenu}
            variant={"ghost"}
            size={"icon"}
          >
            <DotsVerticalIcon className="w-5 h-5" />
          </Button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
              <div className="py-1">
                <Button
                  onClick={() => {
                    closeChat();
                    setMenuOpen(false);
                  }}
                  variant={"ghost"}
                  className="w-full justify-start text-sm text-gray-700 hover:bg-gray-100"
                >
                  Close chat
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
